import React, { forwardRef, Fragment, useCallback, useRef, useState, } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import IconButton, { IconButtonVariants, } from '@polestar/component-warehouse-react/components/icon-button/IconButton';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import { MediaSection, SectionInner, } from '@polestar/component-warehouse-react/components/section/Section';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Expandable from '@polestar/component-warehouse-react/components/expandable/Expandable';
import Sizes from '@polestar/component-warehouse-react/tokens/sizes/Sizes';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import Alignments, { CrossAxisAlignments, MainAxisAlignments, } from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import { LightThemeProvider } from '@polestar/component-warehouse-react/components/theme-providers/ThemeProviders';
import { TextAnchorButton, TextButton, } from '@polestar/component-warehouse-react/components/button/Button';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import Icon, { IconSizes } from '@polestar/component-warehouse-react/components/icon/Icon';
export var BannerBarVariants;
(function (BannerBarVariants) {
    BannerBarVariants["default"] = "default";
    BannerBarVariants["critical"] = "critical";
})(BannerBarVariants || (BannerBarVariants = {}));
const BannerBar = forwardRef(({ children, variant = BannerBarVariants.default, message, closeLabel = 'Close', withCloseButton = true, actionButtonLabel, actionButtonProps = {}, closeButtonProps = {}, onChange, onClose, ...props }, ref) => {
    // TODO: remove when closeLabel is removed
    if (closeLabel && (closeButtonProps === null || closeButtonProps === void 0 ? void 0 : closeButtonProps['aria-label']) === undefined) {
        closeButtonProps['aria-label'] = closeLabel;
    }
    const memoizedId = useSSRSafeId('banner-bar', props.id);
    const messageId = `${memoizedId}-message`;
    const expandableId = `${memoizedId}-expandable`;
    const [open, setOpen] = useState(false);
    const messageRef = useRef(null);
    const isReadMoreALink = (actionButtonProps === null || actionButtonProps === void 0 ? void 0 : actionButtonProps.href) !== undefined;
    const readMoreHandler = useCallback(() => {
        if (!isReadMoreALink) {
            setOpen((val) => {
                onChange === null || onChange === void 0 ? void 0 : onChange(!val);
                return !val;
            });
        }
    }, [onChange, isReadMoreALink]);
    const hasChildren = children !== undefined;
    const closedLabel = typeof actionButtonLabel === 'string' ? actionButtonLabel : actionButtonLabel === null || actionButtonLabel === void 0 ? void 0 : actionButtonLabel.closed;
    const expandedLabel = typeof actionButtonLabel === 'string' ? actionButtonLabel : actionButtonLabel === null || actionButtonLabel === void 0 ? void 0 : actionButtonLabel.expanded;
    const ReadMoreButtonComponent = actionButtonProps.href
        ? ReadMoreAnchorButton
        : ReadMoreButton;
    const readMore = actionButtonLabel && (React.createElement(ReadMoreButtonComponent, { icon: hasChildren ? (open ? Icons.chevronUp : Icons.chevronDown) : Icons.arrowRight, iconPosition: Alignments.right, href: "#", ...actionButtonProps, onClick: readMoreHandler, ...aria({
            controls: expandableId,
            expanded: open,
        }), variant: variant, monochrome: true }, isReadMoreALink ? closedLabel : open ? expandedLabel : closedLabel));
    const handleOnClose = useCallback((e) => {
        onClose === null || onClose === void 0 ? void 0 : onClose(e);
    }, [onClose]);
    return (React.createElement(MediaSection, { ...props, ref: ref, id: memoizedId },
        React.createElement(Wrapper, { variant: variant },
            React.createElement(ThemeWrapper, { fragment: variant !== BannerBarVariants.critical },
                React.createElement(SectionInnerBannerBar, { variant: variant },
                    variant === BannerBarVariants.critical && (React.createElement(CriticalIcon, { icon: Icons.warning, variant: variant, size: IconSizes.small })),
                    React.createElement(MessageWrapper, { orientation: isReadMoreALink ? Orientations.horizontal : Orientations.vertical, withCloseButton: withCloseButton },
                        React.createElement(Message, { id: messageId, variant: variant, ref: messageRef, hasChildren: hasChildren, hasLink: isReadMoreALink }, message),
                        hasChildren && open && (React.createElement(Expandable, { id: expandableId, open: open, ...aria({
                                labelledby: messageId,
                            }) },
                            React.createElement(ExpandableMessage, null, children))),
                        readMore,
                        withCloseButton && (React.createElement(CloseButton, { ...aria({ label: 'Close' }), ...closeButtonProps, icon: Icons.close, bannerVariant: variant, variant: IconButtonVariants.transparent, size: Sizes.large, alignmentOffset: Alignments.right, onClick: handleOnClose }))))))));
});
BannerBar.displayName = 'BannerBar';
export default BannerBar;
const MessageWrapper = styled('div', shouldNotForwardProps(false, 'orientation', 'withCloseButton'))(({ orientation, withCloseButton }) => css(css `
      position: relative;
      display: flex;
      flex-direction: ${orientation === Orientations.horizontal ? 'row' : 'column'};
      flex-wrap: wrap;
      justify-content: ${MainAxisAlignments.start};
      align-items: ${CrossAxisAlignments.start};
      width: 100%;
    `, withCloseButton &&
    css `
        ${responsiveStyles({
        paddingInlineEnd: { mobile: Spacings.xLarge, tablet: Spacings.xxLarge },
    })}
      `));
const SectionInnerBannerBar = styled(SectionInner)(({ variant }) => css `
    display: ${variant === BannerBarVariants.critical ? 'flex' : 'inherit'};
  `);
const Wrapper = styled.div(({ theme, variant }) => {
    var _a;
    return css `
    background-color: ${((_a = theme.bannerBar.variants[variant]) === null || _a === void 0 ? void 0 : _a.background) ||
        theme.bannerBar.variants[BannerBarVariants.default].background};

    ${responsiveStyles({
        paddingBlockStart: {
            mobile: Spacings.xSmall,
            tablet: Spacings.medium,
            desktop: Spacings.large,
        },
        paddingBlockEnd: {
            mobile: Spacings.medium,
            tablet: Spacings.large,
            desktop: Spacings.xLarge,
        },
    })}
  `;
});
const ExpandableMessage = styled.div `
  margin: ${Spacings.medium} 0 ${Spacings.xSmall};
`;
const CloseButton = styled(IconButton, shouldNotForwardProps(false, 'bannerVariant'))(({ theme, bannerVariant }) => {
    var _a, _b, _c, _d;
    return css `
    position: absolute;
    margin-inline-end: -${Spacings.medium};
    inset-inline-end: 0;
    inset-block-start: -${Spacings.xSmall};
    color: ${(_b = (_a = theme.bannerBar.variants[bannerVariant]) === null || _a === void 0 ? void 0 : _a.closeButton) === null || _b === void 0 ? void 0 : _b.enabled};

    &:hover {
      color: ${(_d = (_c = theme.bannerBar.variants[bannerVariant]) === null || _c === void 0 ? void 0 : _c.closeButton) === null || _d === void 0 ? void 0 : _d.hover};
    }
  `;
});
const CriticalIcon = styled(Icon)(({ theme, variant }) => {
    var _a;
    return css `
    margin-inline-end: ${Spacings.xSmall};
    vertical-align: middle;
    > svg {
      transform: translateY(${Spacings.xSmall});
      color: ${(_a = theme.bannerBar.variants[variant]) === null || _a === void 0 ? void 0 : _a.iconColor};
    }
  `;
});
const Message = styled.div(({ theme, variant, hasChildren, hasLink }) => {
    var _a;
    return css `
    color: ${((_a = theme.bannerBar.variants[variant]) === null || _a === void 0 ? void 0 : _a.color) ||
        theme.bannerBar.variants[BannerBarVariants.default].color};
    margin-block-start: ${Spacings.xSmall};
    padding-inline-end: ${Spacings.medium};

    ${responsiveStyles({
        paddingInlineEnd: hasLink ? { mobile: Spacings.xSmall } : { mobile: 0 },
        width: { mobile: '100%', tablet: hasChildren ? '100%' : 'auto' },
    })}
  `;
});
const ReadMoreButton = styled(TextButton)(({ variant, theme }) => {
    var _a;
    return css `
    margin-block-start: ${Spacings.xSmall};
    display: inline-flex;
    align-items: center;
    [data-type='icon'] {
      color: ${variant === BannerBarVariants.critical
        ? (_a = theme.bannerBar.variants[variant]) === null || _a === void 0 ? void 0 : _a.readMoreButton.iconColor
        : undefined};
    }
  `;
});
const ReadMoreAnchorButton = styled(TextAnchorButton)(({ variant, theme }) => {
    var _a;
    return css `
    margin-block-start: ${createCustomSpacing(7 / 16)};
    display: inline-flex;
    align-items: center;
    [data-type='icon'] {
      color: ${variant === BannerBarVariants.critical
        ? (_a = theme.bannerBar.variants[variant]) === null || _a === void 0 ? void 0 : _a.readMoreButton.iconColor
        : undefined};
    }
  `;
});
// ensure that in dark theme all slots are filled with the right color settings
const ThemeWrapper = ({ children, fragment, }) => {
    if (fragment) {
        return React.createElement(Fragment, null, children);
    }
    return React.createElement(LightThemeProvider, null, children);
};
